import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import { useNavigate } from "react-router-dom";
import { batchDetailsWithStudents, batchList } from "../../api/Batch";
import { messEnroll, messList } from "../../api/Mess";
import { StaffList } from "../../api/Staff";
import MessLayout from "./MessLayout";

export default function MessEnroll() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [messes, setMesses] = useState([]);

  /* ============================================================
     API FUNCTIONS
  ============================================================ */

  const getMessList = async () => {
    setLoading(true);

    try {
      const response = await messList(`limit=all&status=1`);

      if (response.data?.status) {
        setMesses(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getStaffs = async () => {
    setLoading(true);

    try {
      const response = await StaffList(`limit=all&status=1`);

      if (response.data?.status) {
        setStaffs(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getBatchesList = async () => {
    setLoading(true);

    try {
      const response = await batchList();

      if (response.data?.status) {
        setBatches(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getBatchStudents = async (id) => {
    if (!id) {
      setStudents([]);
      return;
    }

    setLoading(true);

    try {
      const response = await batchDetailsWithStudents(`id=${id}`);

      if (response.data?.status) {
        setStudents(response?.data?.data?.active_batch_students || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMessList();
    getStaffs();
    getBatchesList();
  }, []);

  /* ============================================================
     FORM SUBMIT
  ============================================================ */

  const handleFrom = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await messEnroll(formData);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);

        navigate("/mess/enroll/list");
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     HELPERS
  ============================================================ */

  const updateData = (key, value) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleTypeChange = (type) => {
    setData({
      type,
    });

    setStudents([]);
  };

  const handleStudentChange = (studentId) => {
    const student = students?.find(
      (s) => String(s?.student_info?.id) === String(studentId),
    )?.student_info;

    if (!student) {
      updateData("student_id", studentId);
      return;
    }

    setData((prev) => ({
      ...prev,
      student_id: studentId,
      name: student?.name || "",
      father_name: student?.father_name || "",
      mobile: student?.phone || "",
      address: [
        student?.village,
        student?.district,
        student?.state,
        student?.zipcode,
      ]
        .filter(Boolean)
        .join(", "),
    }));
  };

  const handleStaffChange = (staffId) => {
    const staff = staffs?.find((s) => String(s?.id) === String(staffId));

    if (!staff) {
      updateData("staff_id", staffId);
      return;
    }

    setData((prev) => ({
      ...prev,
      staff_id: staffId,
      name: staff?.name || "",
      father_name: staff?.father_name || "",
      mobile: staff?.mobile || "",
      address: [staff?.village, staff?.district, staff?.state, staff?.zipcode]
        .filter(Boolean)
        .join(", "),
    }));
  };

  const getSelectedMember = () => {
    if (data?.type === "Student") {
      return students?.find(
        (s) => String(s?.student_info?.id) === String(data?.student_id),
      )?.student_info;
    }

    if (data?.type === "Staff") {
      return staffs?.find((s) => String(s?.id) === String(data?.staff_id));
    }

    return null;
  };

  const selectedMember = getSelectedMember();

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row">
                  <MessLayout />

                  <div className="col-12">
                    {/* ==================================================
                        PAGE HEADER
                    ================================================== */}

                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                              style={{
                                width: "52px",
                                height: "52px",
                              }}
                            >
                              <i className="ri-restaurant-2-line ri-26px"></i>
                            </div>

                            <div>
                              <h4 className="mb-1">New Mess Enrollment</h4>

                              <p className="text-muted mb-0">
                                Enroll a student, staff member, or guest into a
                                mess.
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-label-secondary"
                            onClick={() => navigate("/mess/enroll/list")}
                          >
                            <i className="ri-arrow-left-line me-1"></i>
                            Back to List
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        FORM
                    ================================================== */}

                    <form onSubmit={handleFrom}>
                      {/* ==================================================
                          BASIC ENROLLMENT
                      ================================================== */}

                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header border-bottom py-3">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-label-primary rounded p-2">
                              <i className="ri-restaurant-line"></i>
                            </span>

                            <div>
                              <h5 className="mb-0">Enrollment Details</h5>

                              <small className="text-muted">
                                Select the enrollment type and mess.
                              </small>
                            </div>
                          </div>
                        </div>

                        <div className="card-body">
                          {/* Type Selection */}

                          <div className="mb-4">
                            <label className="form-label fw-medium">
                              Enrollment Type
                              <span className="text-danger"> *</span>
                            </label>

                            <div className="row g-3">
                              {/* Student */}
                              <div className="col-md-4">
                                <button
                                  type="button"
                                  className={`w-100 text-start border rounded-3 p-3 ${
                                    data?.type === "Student"
                                      ? "border-primary bg-label-primary"
                                      : "bg-transparent"
                                  }`}
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleTypeChange("Student")}
                                >
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className={`rounded-3 d-flex align-items-center justify-content-center ${
                                        data?.type === "Student"
                                          ? "bg-primary text-white"
                                          : "bg-label-primary"
                                      }`}
                                      style={{
                                        width: "46px",
                                        height: "46px",
                                      }}
                                    >
                                      <i className="ri-graduation-cap-line ri-22px"></i>
                                    </div>

                                    <div>
                                      <h6 className="mb-1">Student</h6>

                                      <small className="text-muted">
                                        Enroll a student
                                      </small>
                                    </div>
                                  </div>
                                </button>
                              </div>

                              {/* Staff */}
                              <div className="col-md-4">
                                <button
                                  type="button"
                                  className={`w-100 text-start border rounded-3 p-3 ${
                                    data?.type === "Staff"
                                      ? "border-info bg-label-info"
                                      : "bg-transparent"
                                  }`}
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleTypeChange("Staff")}
                                >
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className={`rounded-3 d-flex align-items-center justify-content-center ${
                                        data?.type === "Staff"
                                          ? "bg-info text-white"
                                          : "bg-label-info"
                                      }`}
                                      style={{
                                        width: "46px",
                                        height: "46px",
                                      }}
                                    >
                                      <i className="ri-user-line ri-22px"></i>
                                    </div>

                                    <div>
                                      <h6 className="mb-1">Staff</h6>

                                      <small className="text-muted">
                                        Enroll staff member
                                      </small>
                                    </div>
                                  </div>
                                </button>
                              </div>

                              {/* Guest */}
                              <div className="col-md-4">
                                <button
                                  type="button"
                                  className={`w-100 text-start border rounded-3 p-3 ${
                                    data?.type === "Guest"
                                      ? "border-warning bg-label-warning"
                                      : "bg-transparent"
                                  }`}
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleTypeChange("Guest")}
                                >
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className={`rounded-3 d-flex align-items-center justify-content-center ${
                                        data?.type === "Guest"
                                          ? "bg-warning text-white"
                                          : "bg-label-warning"
                                      }`}
                                      style={{
                                        width: "46px",
                                        height: "46px",
                                      }}
                                    >
                                      <i className="ri-user-star-line ri-22px"></i>
                                    </div>

                                    <div>
                                      <h6 className="mb-1">Guest</h6>

                                      <small className="text-muted">
                                        Temporary enrollment
                                      </small>
                                    </div>
                                  </div>
                                </button>
                              </div>
                            </div>

                            {/* Hidden Type Input */}

                            <input
                              type="hidden"
                              name="type"
                              value={data?.type || ""}
                              required
                            />
                          </div>

                          {/* Mess */}

                          <div className="row">
                            <div className="col-lg-6">
                              <label className="form-label fw-medium">
                                Select Mess
                                <span className="text-danger"> *</span>
                              </label>

                              <div className="input-group">
                                <span className="input-group-text">
                                  <i className="ri-restaurant-2-line"></i>
                                </span>

                                <select
                                  className="form-select"
                                  id="mess_id"
                                  name="mess_id"
                                  required
                                >
                                  <option value="">Select Mess</option>

                                  {messes?.map((mess) => (
                                    <option key={mess?.id} value={mess?.id}>
                                      {mess?.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ==================================================
                          MEMBER SELECTION
                      ================================================== */}

                      {data?.type && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-header border-bottom py-3">
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-label-info rounded p-2">
                                <i className="ri-user-search-line"></i>
                              </span>

                              <div>
                                <h5 className="mb-0">
                                  {data?.type === "Guest"
                                    ? "Guest Details"
                                    : "Member Selection"}
                                </h5>

                                <small className="text-muted">
                                  {data?.type === "Student"
                                    ? "Select batch and student."
                                    : data?.type === "Staff"
                                      ? "Select the staff member."
                                      : "Enter guest information."}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="card-body">
                            {/* Student Selection */}

                            {data?.type === "Student" && (
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <label className="form-label">
                                    Batch
                                    <span className="text-danger"> *</span>
                                  </label>

                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <i className="ri-book-open-line"></i>
                                    </span>

                                    <select
                                      className="form-select"
                                      id="batch_id"
                                      name="batch_id"
                                      value={data?.batch_id || ""}
                                      required
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        getBatchStudents(value);

                                        setData((prev) => ({
                                          ...prev,
                                          batch_id: value,
                                          student_id: "",
                                          name: "",
                                          father_name: "",
                                          mobile: "",
                                          address: "",
                                        }));
                                      }}
                                    >
                                      <option value="">Select Batch</option>

                                      {batches?.map((batch) => (
                                        <option
                                          key={batch?.id}
                                          value={batch?.id}
                                        >
                                          {batch?.title} ({batch?.course?.title}
                                          )
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>

                                <div className="col-md-6">
                                  <label className="form-label">
                                    Student
                                    <span className="text-danger"> *</span>
                                  </label>

                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <i className="ri-user-line"></i>
                                    </span>

                                    <select
                                      className="form-select"
                                      name="student_id"
                                      value={data?.student_id || ""}
                                      required
                                      disabled={!data?.batch_id}
                                      onChange={(e) =>
                                        handleStudentChange(e.target.value)
                                      }
                                    >
                                      <option value="">
                                        {data?.batch_id
                                          ? "Select Student"
                                          : "Select Batch First"}
                                      </option>

                                      {students?.map((student) => (
                                        <option
                                          key={student?.student_info?.id}
                                          value={student?.student_info?.id}
                                        >
                                          {student?.student_info?.name} (
                                          {
                                            student?.student_info
                                              ?.registration_no
                                          }
                                          )
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {data?.batch_id && students?.length === 0 && (
                                    <small className="text-danger mt-1 d-block">
                                      No active students found in this batch.
                                    </small>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Staff Selection */}

                            {data?.type === "Staff" && (
                              <div className="row">
                                <div className="col-md-6">
                                  <label className="form-label">
                                    Staff Member
                                    <span className="text-danger"> *</span>
                                  </label>

                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <i className="ri-user-line"></i>
                                    </span>

                                    <select
                                      className="form-select"
                                      name="staff_id"
                                      value={data?.staff_id || ""}
                                      required
                                      onChange={(e) =>
                                        handleStaffChange(e.target.value)
                                      }
                                    >
                                      <option value="">Select Staff</option>

                                      {staffs?.map((staff) => (
                                        <option
                                          key={staff?.id}
                                          value={staff?.id}
                                        >
                                          {staff?.name} (
                                          {staff?.registration_no})
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* ==================================================
                          PERSONAL DETAILS
                      ================================================== */}

                      {data?.type && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-header border-bottom py-3">
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-label-success rounded p-2">
                                <i className="ri-user-settings-line"></i>
                              </span>

                              <div>
                                <h5 className="mb-0">Personal Details</h5>

                                <small className="text-muted">
                                  Verify or update the enrollment information.
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="card-body">
                            {/* Selected member preview */}

                            {selectedMember && data?.type !== "Guest" && (
                              <div className="alert bg-label-primary border-0 d-flex align-items-center gap-3 mb-4">
                                <div
                                  className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "46px",
                                    height: "46px",
                                  }}
                                >
                                  <i
                                    className={
                                      data?.type === "Student"
                                        ? "ri-graduation-cap-line ri-20px"
                                        : "ri-user-line ri-20px"
                                    }
                                  ></i>
                                </div>

                                <div>
                                  <h6 className="mb-1">
                                    {selectedMember?.name}
                                  </h6>

                                  <small>
                                    {data?.type === "Student"
                                      ? `Registration No: ${
                                          selectedMember?.registration_no || "-"
                                        }`
                                      : `Staff ID: ${
                                          selectedMember?.registration_no || "-"
                                        }`}
                                  </small>
                                </div>
                              </div>
                            )}

                            <div className="row g-3">
                              {/* Name */}

                              <div className="col-md-6">
                                <label className="form-label">
                                  Name
                                  <span className="text-danger"> *</span>
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="ri-user-line"></i>
                                  </span>

                                  <input
                                    type="text"
                                    className="form-control input-uppercase"
                                    placeholder="Enter name"
                                    name="name"
                                    required
                                    value={data?.name || ""}
                                    onChange={(e) =>
                                      updateData("name", e.target.value)
                                    }
                                  />
                                </div>
                              </div>

                              {/* Father Name */}

                              {(data?.type === "Student" ||
                                data?.type === "Staff") && (
                                <div className="col-md-6">
                                  <label className="form-label">
                                    Father Name
                                  </label>

                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <i className="ri-parent-line"></i>
                                    </span>

                                    <input
                                      type="text"
                                      className="form-control input-uppercase"
                                      placeholder="Enter father name"
                                      name="father_name"
                                      value={data?.father_name || ""}
                                      onChange={(e) =>
                                        updateData(
                                          "father_name",
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Mobile */}

                              <div className="col-md-6">
                                <label className="form-label">
                                  Mobile
                                  <span className="text-danger"> *</span>
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="ri-phone-line"></i>
                                  </span>

                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter mobile number"
                                    name="mobile"
                                    required
                                    value={data?.mobile || ""}
                                    onChange={(e) =>
                                      updateData("mobile", e.target.value)
                                    }
                                  />
                                </div>
                              </div>

                              {/* Address */}

                              <div className="col-md-6">
                                <label className="form-label">Address</label>

                                <div className="input-group">
                                  <span className="input-group-text align-items-start pt-3">
                                    <i className="ri-map-pin-line"></i>
                                  </span>

                                  <textarea
                                    className="form-control input-uppercase"
                                    rows="1"
                                    placeholder="Enter address"
                                    name="address"
                                    value={data?.address || ""}
                                    onChange={(e) =>
                                      updateData("address", e.target.value)
                                    }
                                  ></textarea>
                                </div>
                              </div>

                              {/* Guest Days */}

                              {data?.type === "Guest" && (
                                <div className="col-md-6">
                                  <label className="form-label">
                                    Number of Days
                                    <span className="text-danger"> *</span>
                                  </label>

                                  <div className="input-group">
                                    <span className="input-group-text">
                                      <i className="ri-calendar-2-line"></i>
                                    </span>

                                    <input
                                      type="number"
                                      className="form-control"
                                      placeholder="Enter number of days"
                                      name="days"
                                      min="1"
                                      required
                                    />
                                  </div>

                                  <small className="text-muted">
                                    Enter the number of days the guest will use
                                    the mess.
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ==================================================
                          ENROLLMENT DATE
                      ================================================== */}

                      {data?.type && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-header border-bottom py-3">
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-label-warning rounded p-2">
                                <i className="ri-calendar-event-line"></i>
                              </span>

                              <div>
                                <h5 className="mb-0">Enrollment Period</h5>

                                <small className="text-muted">
                                  Set the date from which this enrollment
                                  becomes active.
                                </small>
                              </div>
                            </div>
                          </div>

                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6">
                                <label className="form-label">
                                  Joining Date
                                  <span className="text-danger"> *</span>
                                </label>

                                <div className="input-group">
                                  <span className="input-group-text">
                                    <i className="ri-calendar-check-line"></i>
                                  </span>

                                  <input
                                    type="date"
                                    className="form-control"
                                    name="joining_date"
                                    required
                                    defaultValue={
                                      new Date().toISOString().split("T")[0]
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ==================================================
                          SUBMIT FOOTER
                      ================================================== */}

                      {data?.type && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-body">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                              <div className="d-flex align-items-center gap-2 text-muted">
                                <i className="ri-information-line ri-20px"></i>

                                <span className="small">
                                  Please verify the details before submitting
                                  the enrollment.
                                </span>
                              </div>

                              <div className="d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-label-secondary"
                                  onClick={() => navigate("/mess/enroll/list")}
                                  disabled={loading}
                                >
                                  Cancel
                                </button>

                                <button
                                  type="submit"
                                  className="btn btn-primary px-4"
                                  disabled={loading}
                                >
                                  {loading ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-save-line me-1"></i>
                                      Save Enrollment
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
